import {useEffect, useState} from "preact/hooks";
import {MegaphoneIcon} from "@heroicons/react/24/solid";
import messages from "../messages/messages.js";
import {fetchEventList, liveEvents, pickBannerEvent} from "../includes/event.js";

// Stage-appropriate call to action shown next to the event title. 'ended' is
// resolved in ctaFor() because its wording depends on whether the event had
// voting.
const STAGE_CTA = {
    announced: "Coming soon — learn more",
    signups: "Sign-ups are open — join now",
    submissions: "Submissions are open — enter now",
    voting: "Voting is open — cast your votes",
    closed: "Learn more"
};

// An ended event with an active banner advertises its aftermath — the
// results (voting events) or the entries (non-voting) — never "coming soon".
const ctaFor = (event) => {
    if (event.stage === "ended") {
        return event.voting_enabled
            ? messages["EVENT.BANNER.SEE_RESULTS"]
            : messages["EVENT.BANNER.SEE_ENTRIES"];
    }
    return STAGE_CTA[event.stage] || "Learn more";
};

const BANNER_CLASS = "mt-14 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-zinc-800 via-zinc-700 to-zinc-800 hover:via-zinc-600 text-sm text-white py-2 px-4 transition-colors";

// Slim site-wide banner shown under the header while an event is live
// (banner window active, or any signup/submission/voting window open).
// One live event links straight to it; two or more collapse into a single
// aggregated banner pointing at the /events directory. Renders nothing when
// no event is live.
export function EventBanner() {
    const [banner, setBanner] = useState(null);

    useEffect(() => {
        // Public, CDN-cached list — fetched once per page load. Failures
        // simply leave the banner hidden.
        fetchEventList()
            .then(({events, serverTime}) => {
                const live = liveEvents(events);
                if (live.length === 0) return;
                setBanner({count: live.length, event: pickBannerEvent(events, serverTime)});
            }).catch(() => {});
    }, []);

    if (!banner) return null;

    if (banner.count > 1) {
        return (
            <a href="/events" className={BANNER_CLASS}>
                <MegaphoneIcon className="w-4 h-4 text-red-400 shrink-0" aria-hidden="true"/>
                <span className="font-semibold truncate">
                    {messages["EVENT.BANNER.MULTI"].replace("{count}", banner.count)}
                </span>
            </a>
        );
    }

    const event = banner.event;
    return (
        <a href={`/event?e=${event.id}`} className={BANNER_CLASS}>
            <MegaphoneIcon className="w-4 h-4 text-red-400 shrink-0" aria-hidden="true"/>
            <span className="font-semibold truncate">{event.title}</span>
            <span className="text-gray-400 hidden sm:inline" aria-hidden="true">·</span>
            <span className="text-gray-300 hidden sm:inline truncate">{ctaFor(event)}</span>
        </a>
    );
}
